import {
  AcademicCapIcon,
  BookOpenIcon,
  CalendarIcon,
  CashIcon,
  ChatIcon,
  ClipboardListIcon,
  ClockIcon,
  CollectionIcon,
  CursorClickIcon,
  DocumentTextIcon,
  IdentificationIcon,
  OfficeBuildingIcon,
  UserAddIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import type { IndustryContent } from '../types';
import EnquiryForm from '../visuals/EnquiryForm';
import { AgentBubble } from '../../ChatBubbles';
import { ArrowGlyph, CheckGlyph, tone } from '../../primitives';

const BRAND_BG = 'bg-[#8c1c3c]';
const BRAND_NAME = 'Larkspur College';

export function useEducationContent(): IndustryContent {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');

  return {
    key: 'education',
    seoTitle: t('Education Chatbot: AI Agent for Students and Admissions'),
    seoDescription: t(
      'Answer applicants and students at any hour. An AI agent for schools, colleges and course providers that explains admissions, deadlines, fees and courses from your own documents.',
    ),
    hero: {
      title: t('Answers for every student and applicant.'),
      accent: t('Even the night before the deadline.'),
      lead: t(
        'Train an agent on your prospectus, admissions guides and handbooks. It answers questions about courses, fees and deadlines at any hour, and collects enquiries for your admissions team.',
      ),
      description: t(
        'Example: an applicant asks a college when applications close. The agent gives the date, lists the documents needed and links to the applicant portal.',
      ),
      surface: 'widget',
      brand: {
        name: BRAND_NAME,
        initial: 'L',
        kind: t('College'),
        bg: BRAND_BG,
      },
      messages: [
        {
          from: 'visitor',
          text: tc('When do applications close for the autumn intake?'),
          at: 600,
        },
        {
          from: 'agent',
          typingAt: 1100,
          at: 1900,
          content: tc(
            'On 30 June. Would you like the checklist of documents you need?',
          ),
        },
        { from: 'visitor', text: t('Yes, please.'), at: 2900 },
        {
          from: 'agent',
          typingAt: 3300,
          at: 4100,
          content: <DocumentChecklist />,
        },
        {
          from: 'agent',
          at: 4800,
          content: <PortalLink />,
        },
      ],
      steps: [
        {
          icon: BookOpenIcon,
          title: t('Found the deadline in the admissions guide'),
          via: tc('Knowledge'),
          at: 1400,
        },
        {
          icon: ClipboardListIcon,
          title: t('Listed the documents needed'),
          via: tc('Knowledge'),
          at: 3600,
        },
        {
          icon: CursorClickIcon,
          title: t('Linked the applicant portal'),
          via: tc('Buttons'),
          at: 4600,
        },
      ],
    },
    jobs: {
      title: t('The questions that fill your inbox every term.'),
      tail: t('Answered in seconds, at any hour.'),
      lead: t(
        'Applicants and students ask the same things year after year. Your agent answers them from your own documents, so your staff can spend their time with the people who need them.',
      ),
      items: [
        {
          icon: AcademicCapIcon,
          title: t('Admissions'),
          detail: t(
            'Explains entry requirements, deadlines and how to apply, step by step.',
          ),
        },
        {
          icon: BookOpenIcon,
          title: t('Courses'),
          detail: t(
            'Answers questions about modules, schedules and course content from your prospectus.',
          ),
        },
        {
          icon: CashIcon,
          title: t('Fees and funding'),
          detail: t(
            'Explains tuition fees, payment plans and scholarships from your published information.',
          ),
        },
        {
          icon: OfficeBuildingIcon,
          title: t('Campus life'),
          detail: t(
            'Shares information about housing, facilities and student support.',
          ),
        },
        {
          icon: UserAddIcon,
          title: t('Enquiries and open days'),
          detail: t(
            'Collects contact details from prospective students and sends them to your admissions team.',
          ),
        },
        {
          icon: ClockIcon,
          title: t('Everyday student questions'),
          detail: t(
            'Helps current students with timetables, policies and where to find things.',
          ),
        },
      ],
    },
    workflow: {
      title: t('Trained on your documents.'),
      tail: t('Answering wherever students are.'),
      learns: [
        { icon: BookOpenIcon, label: t('Prospectus and course pages') },
        { icon: DocumentTextIcon, label: t('Admissions guides') },
        { icon: CollectionIcon, label: t('Student handbook') },
        { icon: VideoCameraIcon, label: t('Open day talks on YouTube') },
      ],
      acts: [
        { icon: IdentificationIcon, label: tc('Collect data') },
        { icon: ClipboardListIcon, label: tc('Forms') },
        { icon: CursorClickIcon, label: tc('Buttons') },
        { icon: CalendarIcon, label: tc('Google Calendar') },
      ],
      channels: [
        { icon: ChatIcon, label: tc('Website chat') },
        { icon: 'FaWhatsapp', label: 'WhatsApp' },
        { icon: 'FaTelegram', label: 'Telegram' },
        { icon: 'FaFacebookMessenger', label: 'Messenger' },
        { icon: 'FaEnvelope', label: tc('Email') },
      ],
    },
    spotlight: {
      title: t('Every enquiry,'),
      tail: t('captured and passed on.'),
      lead: t(
        'When a prospective student is interested, the agent asks for their details in the chat and sends them straight to your admissions team.',
      ),
      points: [
        t('A short form in the chat after the first answer'),
        t('An email to your team for every new enquiry'),
        t('All enquiries on one page, ready to export as CSV'),
        t('Or let the agent ask for details as part of the conversation'),
      ],
      description: t(
        'Example: a contact form inside the college’s chat, filled in by a prospective student, and the email the admissions team receives for it.',
      ),
      visual: <EnquiryForm brand={BRAND_BG} name={BRAND_NAME} />,
    },
    faqs: [
      {
        question: t('Can it answer from our prospectus and handbooks?'),
        answer: t(
          'Yes. Upload PDF and Word files and add your website. You can also add YouTube videos, such as open day talks, and the agent learns from what is said in them. Retrain with one click when something changes.',
        ),
      },
      {
        question: t('Can students use it on WhatsApp or Telegram?'),
        answer: t(
          'Yes. Connect WhatsApp, Telegram, Messenger or LINE and the same agent answers there, with the same knowledge as on your website.',
        ),
      },
      {
        question: t('How do we collect enquiries from prospective students?'),
        answer: t(
          'Turn on the lead form to ask for a name, email and phone number in the chat, or add a Collect Data action to ask for the details you need. You get an email for each new enquiry and can export them as CSV.',
        ),
      },
      {
        question: t('Can staff use an agent too?'),
        answer: t(
          'Yes. Train a separate agent on internal documents, such as staff policies or IT guides, and add it to Slack or your intranet.',
        ),
      },
      {
        question: tc('Is there a free plan?'),
        answer: tc(
          'Yes. The Free plan includes one agent and 100 messages a month on your website, Telegram, Messenger and Slack. No credit card needed.',
        ),
      },
    ],
    finalExchange: {
      question: t('When is the next open day?'),
      answer: t('On Saturday 18 October, from 10 a.m. to 2 p.m.'),
    },
    finalTitle: t('Your agent could be answering students by lunchtime.'),
  };
}

/** The documents an applicant needs, as a card in the chat. */
function DocumentChecklist() {
  const { t } = useLocale('bot-use-cases');
  const documents = [
    t('Passport or ID card'),
    t('Academic transcripts'),
    t('Personal statement'),
    t('Two references'),
  ];

  return (
    <div className='w-[85%] rounded-2xl bg-[#f6f6f9] p-3.5 ring-1 ring-black/[0.05] dark:bg-white/[0.05] dark:ring-white/[0.06]'>
      <p className={clsxm('text-[13px] font-semibold', tone.ink)}>
        {t('Documents you’ll need')}
      </p>
      <ul className='mt-2 space-y-1.5'>
        {documents.map((document) => (
          <li
            key={document}
            className={clsxm('flex items-center gap-2 text-[13px]', tone.body)}
          >
            <span
              className={clsxm(
                'flex h-4 w-4 flex-none items-center justify-center rounded text-white',
                BRAND_BG,
              )}
            >
              <CheckGlyph className='h-3 w-3' />
            </span>
            {document}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The agent's closing answer with a button to the applicant portal. */
function PortalLink() {
  const { t } = useLocale('bot-use-cases');

  return (
    <div className='flex max-w-[88%] flex-col items-start gap-2'>
      <AgentBubble brand={BRAND_BG}>
        {t('You can upload them all in the applicant portal.')}
      </AgentBubble>
      <span
        className={clsxm(
          'inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[13px] font-semibold ring-1 ring-black/10 dark:bg-white/[0.06] dark:ring-white/15',
          tone.ink,
        )}
      >
        {t('Open the applicant portal')}
        <ArrowGlyph className='h-3.5 w-3.5' />
      </span>
    </div>
  );
}
